// TanStack Query hooks for the Principals module. Principals are the protectees
// under executive / close protection — heavy PII (DOB, medical notes,
// residences, vehicles). Every endpoint is gated server-side on principal.*, so
// callers should additionally gate the UI on can('principal.read' / .write /
// .delete). Mirrors packages/api/src/routes/principals.ts.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreatePrincipalInput,
  Principal,
  PrincipalResponse,
  PrincipalRiskLevel,
  PrincipalsListResponse,
  UpdatePrincipalInput,
} from '@dsr/shared-types';
import { api } from '../client';

export interface PrincipalFilters {
  organization_id?: number;
  risk_level?: PrincipalRiskLevel;
  q?: string;
}

export function usePrincipals(
  filters: PrincipalFilters = {},
  options: { enabled?: boolean } = {},
) {
  const params = new URLSearchParams();
  if (filters.organization_id !== undefined) {
    params.set('organization_id', String(filters.organization_id));
  }
  if (filters.risk_level) params.set('risk_level', filters.risk_level);
  if (filters.q && filters.q.trim().length > 0) params.set('q', filters.q.trim());
  const qs = params.toString();
  const url = qs.length > 0 ? `/api/principals?${qs}` : '/api/principals';
  return useQuery({
    queryKey: ['principals', { ...filters }],
    enabled: options.enabled ?? true,
    queryFn: ({ signal }) => api.get<PrincipalsListResponse>(url, signal),
  });
}

export function usePrincipal(id: number | null) {
  return useQuery({
    queryKey: ['principal', id],
    enabled: id !== null && id > 0,
    queryFn: ({ signal }) =>
      api.get<PrincipalResponse>(`/api/principals/${id}`, signal),
  });
}

export function useCreatePrincipal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePrincipalInput) =>
      api.post<PrincipalResponse>('/api/principals', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['principals'] });
    },
  });
}

export interface UpdatePrincipalVars extends UpdatePrincipalInput {
  id: number;
}

export function useUpdatePrincipal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdatePrincipalVars) =>
      api.patch<PrincipalResponse>(`/api/principals/${id}`, patch),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['principals'] });
      void qc.invalidateQueries({ queryKey: ['principal', vars.id] });
    },
  });
}

export function useDeletePrincipal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/principals/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['principals'] });
    },
  });
}

// Re-export the wire type callers most often need alongside the hooks.
export type { Principal };
